import { computed, unref, type Ref } from 'vue';
import { dinero, toDecimal } from 'dinero.js';
import { AUD } from 'dinero.js/currencies';
import { useSessionStore, useAppStore } from '@/store';

type OrderItemId = number | string;

export function usePriceBreakdown(options?: { orderItemIds?: Ref<OrderItemId[]> | OrderItemId[] }) {
    const session = useSessionStore();
    const appStore = useAppStore();

    const orderItemIds = computed<OrderItemId[] | undefined>(() => {
        return (options && options.orderItemIds ? unref(options.orderItemIds) : undefined) as OrderItemId[] | undefined;
    });

    const parseMoneyStringToCents = (formatted: string | null | undefined): number => {
        if (!formatted) return 0;
        const cleaned = formatted.replace(/[$,\s]/g, '');
        const value = parseFloat(cleaned);
        return Number.isFinite(value) ? Math.round(value * 100) : 0;
    };

    const formatCents = (cents: number): string =>
        new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
            parseFloat(toDecimal(dinero({ amount: cents, currency: AUD }))),
        );

    const breakdownItems = computed(() => {
        const items: Array<{
            label: string;
            unitCost: string | null;
            rows: Array<{ label: string; value: string; muted?: boolean }>;
        }> = [];

        const orderItems = (session.order as any)?.items ?? [];

        let confirmableItems = orderItems.filter(
            (item: any) => item?.status !== 'CONFIRMED' || item?.service_status === 'Pending',
        );

        const ids = unref(orderItemIds);
        if (ids && ids.length) {
            const idSet = new Set(ids);
            confirmableItems = confirmableItems.filter((i: any) => idSet.has(i.id));
        }

        // Exclude storage when any non-storage items are present
        const hasNonStorage = confirmableItems.some(
            (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
        );
        if (hasNonStorage) {
            confirmableItems = confirmableItems.filter(
                (i: any) => ((i?.productable_type ?? '') as string).toLowerCase() !== 'storage',
            );
        }

        // Helpers
        const formatDate = (iso: string | null | undefined, tz?: string | null): string => {
            if (!iso) return '-';
            try {
                const date = new Date(iso);
                const formatter = new Intl.DateTimeFormat('en-AU', {
                    timeZone: tz ?? undefined,
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                });
                return formatter.format(date);
            } catch {
                return '-';
            }
        };

        const deriveUnit = (product: any): string | null => {
            return product?.price_unit_formatted;
        };

        confirmableItems.forEach((orderItem: any) => {
            const label = orderItem.service_label ?? orderItem.service_type ?? 'Service';
            let unitCost: string | null = null;
            let isVariable: boolean;
            let isMove = false;

            // STORAGE handling
            const isStorage = ((orderItem.productable_type ?? '') as string).toLowerCase() === 'storage';
            if (isStorage) {
                const product = orderItem.product ?? {};
                const unit = deriveUnit(product);
                const storagePrice = product?.price?.formatted;
                if (storagePrice && unit) {
                    unitCost = `${storagePrice} / ${unit}`;
                } else if (storagePrice) {
                    unitCost = storagePrice;
                }

                const start = formatDate(product?.start_date, product?.timezone?.timezone);
                const end = formatDate(product?.end_date, product?.timezone?.timezone);

                items.push({
                    label,
                    unitCost,
                    rows: [
                        { label: 'Start date', value: start },
                        { label: 'End date', value: end },
                    ],
                });
                return; // proceed next item
            }

            if ((orderItem.productable_type ?? '').toLowerCase() === 'move') {
                const selectedMatch = session.getSelectedMatch;
                const pricingType = selectedMatch?.pricing_type ?? null;
                isVariable = pricingType === 'VARIABLE';
                isMove = true;

                if (session.isLocal && isVariable && selectedMatch?.price_per_unit?.formatted) {
                    if (selectedMatch.price_per_hour) {
                        unitCost = `${selectedMatch.price_per_hour.formatted} / hr`;
                    } else {
                        unitCost = `${selectedMatch.price_per_unit.formatted} / 30 mins`;
                    }
                } else if (!isVariable && orderItem.totals?.TOTAL?.formatted) {
                    const cvpCents = (selectedMatch?.customer_visible_prices ?? []).reduce(
                        (sum: number, p: { value: string }) => sum + parseMoneyStringToCents(p.value),
                        0,
                    );
                    if (cvpCents > 0) {
                        const totalCents = parseMoneyStringToCents(orderItem.totals.TOTAL.formatted);
                        unitCost = formatCents(Math.max(totalCents - cvpCents, 0));
                    } else {
                        unitCost = orderItem.totals.TOTAL.formatted;
                    }
                }
            } else {
                const product = orderItem.product ?? {};
                const rawBasePrice: string | null =
                    product?.price?.formatted ?? orderItem.totals?.TOTAL?.formatted ?? null;
                const basePrice: string | null = rawBasePrice;
                const priceUnit: string | null = product?.price_unit ?? null;
                const priceUnitFormatted: any = product?.price_unit_formatted ?? null;

                // Determine variable status for non-move items
                const productPriceType: string | null = product?.price_type ?? null;
                const depositType: string | null = product?.deposit_type ?? null;
                const quantity: number | null = product?.quantity ?? null;
                isVariable = productPriceType === 'VARIABLE' || depositType === 'METERED' || !!quantity;

                if (basePrice) {
                    if (priceUnit === 'HALF_HOUR') {
                        unitCost = `${basePrice} / 30 mins`;
                    } else if (priceUnitFormatted?.unit_single) {
                        unitCost = `${basePrice} / ${priceUnitFormatted.unit_single}`;
                    } else {
                        unitCost = basePrice;
                    }
                } else {
                    unitCost = null;
                }
            }

            const remaining: string | null =
                isVariable ? null
                : orderItem.totals?.BALANCE?.formatted ? orderItem.totals.BALANCE.formatted
                : null;
            // For interstate moves not within a week, show note next to label
            const remainingLabelNote: string | null =
                isMove && !session.isLocal && session.getSelectedMatch?.within_week != 1 ?
                    (() => {
                        const days = appStore.tenantFinalPaymentDays;
                        if (days > 0) return ` (${days} days before move)`;
                        if (days < 0) return ` (${Math.abs(days)} days after move)`;
                        return ' (on move day)';
                    })()
                :   null;
            const remainingNote = isVariable ? 'To be calculated after service complete' : null;

            const rows: Array<{ label: string; value: string; muted?: boolean }> = [];

            // For Move items, add pricing breakdown sections
            if (isMove) {
                const selectedMatch = session.getSelectedMatch;

                // Call-out fee for variable pricing
                if (selectedMatch?.pricing_type === 'VARIABLE') {
                    const callOutFeeAmount = Number(selectedMatch?.call_out_fee?.amount ?? 0);
                    if (callOutFeeAmount > 0) {
                        const callOutFeeValue =
                            appStore.tenantCallOutFeeAsTime && selectedMatch?.call_out_fee_time ?
                                selectedMatch.call_out_fee_time
                            :   selectedMatch?.call_out_fee?.formatted || '-';
                        rows.push({ label: 'Call-out fee', value: callOutFeeValue });
                    }
                }

                const customerVisiblePrices: Array<{ label: string; value: string }> =
                    selectedMatch?.customer_visible_prices ?? [];
                for (const price of customerVisiblePrices) {
                    rows.push({ label: price.label, value: price.value });
                }

                // Deposit
                rows.push({
                    label: 'Deposit (due now)',
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });

                if (selectedMatch?.pricing_type === 'VARIABLE') {
                    rows.push({
                        label: 'Minimum hours',
                        value: Number(appStore.tenantMinHours).toFixed(0) + ' hours',
                    });
                }

                // Final payment (renamed from Remaining)
                if (appStore.tenantAutoCreateFinal) {
                    if (remainingNote) {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: remainingNote, muted: true });
                    } else {
                        const lbl = remainingLabelNote ? `Final payment${remainingLabelNote}` : 'Final payment';
                        rows.push({ label: lbl, value: (remaining ?? '-') as string });
                    }
                }
            } else {
                // For non-Move items, keep existing structure
                // Deposit row
                rows.push({
                    label: 'Deposit (due now)',
                    value: orderItem.totals?.DEPOSIT?.formatted || '-',
                });
                // Remaining row
                if (remainingNote) {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: remainingNote, muted: true });
                } else {
                    const lbl = remainingLabelNote ? `Remaining${remainingLabelNote}` : 'Remaining';
                    rows.push({ label: lbl, value: (remaining ?? '-') as string });
                }
            }

            items.push({
                label,
                unitCost,
                rows,
            });
        });

        return items;
    });

    const displayPricesExTax = computed(
        () => (appStore.tenant as any)?.company?.settings?.display_prices_ex_tax ?? false,
    );

    const dueNowTotal = computed(() => {
        const computeDueNow = (ids?: Array<number | string>) => {
            const orderItems = (session.order as any)?.items ?? [];
            const idSet = ids && ids.length > 0 ? new Set(ids) : null;
            // Sum the same DEPOSIT.formatted values shown in the deposit rows. The backend already
            // applies any ex-tax / region adjustment to `.formatted` (PricingMoneyData), so the total
            // stays consistent with the displayed rows without re-deriving the tax rate here.
            const cents = orderItems
                .filter((item: any) => item?.status !== 'CONFIRMED')
                // Exclude storage from due now
                .filter((item: any) => ((item?.productable_type ?? '') as string).toLowerCase() !== 'storage')
                .filter((item: any) => (idSet ? idSet.has(item.id) : true))
                .reduce((acc: number, item: any) => acc + parseMoneyStringToCents(item?.totals?.DEPOSIT?.formatted), 0);

            return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
                parseFloat(toDecimal(dinero({ amount: cents, currency: AUD }))),
            );
        };

        const ids = orderItemIds.value as Array<number | string> | undefined;
        return computeDueNow(ids);
    });

    return {
        breakdownItems,
        dueNowTotal,
        displayPricesExTax,
    };
}
